import { useState } from 'react';
import Layout from '../components/layout/Layout';
import SectionLabel from '../components/shared/SectionLabel';
import SEO from '../components/shared/SEO';
import { submitContact } from '../api';
import '../styles/contactpage.css';

const contactInfo = [
  {
    id: 'address-us',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
        <circle cx="12" cy="9" r="2.5" />
      </svg>
    ),
    label: '244 Fifth Ave Suite V255',
    sub: 'New York City, New York 10001\n(Please call before visit)',
  },
  {
    id: 'address-in',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
        <circle cx="12" cy="9" r="2.5" />
      </svg>
    ),
    label: 'Level 11, Platina G Block',
    sub: 'C-59, BKC, Bandra (East)\nMumbai – 400051, Maharashtra',
  },
  {
    id: 'email',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <polyline points="2,4 12,13 22,4" />
      </svg>
    ),
    label: 'info@apexdefilabs.com',
    sub: 'For Clients & Partners',
  },
  {
    id: 'phone',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" />
      </svg>
    ),
    label: '+1 888-666-0441',
    sub: 'Business days 10 AM – 6 PM',
  },
];

type FormState = {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

const emptyForm: FormState = { name: '', email: '', phone: '', company: '', message: '' };

function validateForm(form: FormState): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.name.trim() || form.name.trim().length < 2) {
    errors.name = 'Name is required';
  }
  if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = 'A valid email address is required';
  }
  if (!form.message.trim() || form.message.trim().length < 10) {
    errors.message = 'Message must be at least 10 characters';
  }
  return errors;
}

export default function ContactPage() {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const errors = validateForm(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    setLoading(true);
    try {
      await submitContact({ ...form });
      setSent(true);
      setForm(emptyForm);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <SEO
        title="Contact Us - Apex DeFi Labs"
        description="Get in touch with Apex DeFi Labs. Reach out for client inquiries, partnerships, or general information."
        url="https://apexdefilabs.com/contact"
        type="website"
        keywords="Apex DeFi Labs contact, get in touch, DeFi investment inquiry"
      />

      <div className="contact-page">
        {/* Hero */}
        <section className="contact-hero">
          <div className="contact-hero-overlay" />
          <div className="contact-shell">
            <div style={{ marginBottom: '1.2rem' }}>
              <SectionLabel>Get in Touch</SectionLabel>
            </div>
            <h1 className="contact-hero-title">
              Contact <em>Us</em>
            </h1>
           
          </div>
        </section>

        {/* Contact Info Strip */}
        <section className="contact-info-strip">
          <div className="contact-shell">
            <div className="contact-info-grid">
              {contactInfo.map((item) => (
                <div key={item.id} className="contact-info-card">
                  <div className="contact-info-icon">{item.icon}</div>
                  <p className="contact-info-label">{item.label}</p>
                  <p className="contact-info-sub" style={{ whiteSpace: 'pre-line' }}>{item.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section className="contact-form-section">
          <div className="contact-shell">
            <div className="contact-form-inner">
              <SectionLabel>Send a Message</SectionLabel>
              <h2 className="contact-form-heading">
                Send us a <em>message!</em>
              </h2>

              {sent ? (
                <p style={{ fontFamily: 'DM Sans, sans-serif', color: '#B8860B', fontSize: '1rem', lineHeight: 1.7 }}>
                  Thank you for reaching out. Our team will be in touch shortly.
                </p>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit} noValidate>
                  <div className="contact-form-row">
                    <div className="contact-field">
                      <label className="contact-label">Name <span className="contact-required">*</span></label>
                      <input
                        className={`contact-input${fieldErrors.name ? ' contact-input-error' : ''}`}
                        type="text"
                        placeholder="Your full name"
                        value={form.name}
                        onChange={set('name')}
                      />
                      {fieldErrors.name && <span className="contact-field-error">{fieldErrors.name}</span>}
                    </div>
                    <div className="contact-field">
                      <label className="contact-label">Email <span className="contact-required">*</span></label>
                      <input
                        className={`contact-input${fieldErrors.email ? ' contact-input-error' : ''}`}
                        type="email"
                        placeholder="your@email.com"
                        value={form.email}
                        onChange={set('email')}
                      />
                      {fieldErrors.email && <span className="contact-field-error">{fieldErrors.email}</span>}
                    </div>
                  </div>

                  <div className="contact-form-row">
                    <div className="contact-field">
                      <label className="contact-label">Phone</label>
                      <input
                        className="contact-input"
                        type="tel"
                        placeholder="Phone number"
                        value={form.phone}
                        onChange={set('phone')}
                      />
                    </div>
                    <div className="contact-field">
                      <label className="contact-label">Company</label>
                      <input
                        className="contact-input"
                        type="text"
                        placeholder="Company name"
                        value={form.company}
                        onChange={set('company')}
                      />
                    </div>
                  </div>

                  <div className="contact-field">
                    <label className="contact-label">Message <span className="contact-required">*</span></label>
                    <textarea
                      className={`contact-input contact-textarea${fieldErrors.message ? ' contact-input-error' : ''}`}
                      placeholder="Write your message here…"
                      value={form.message}
                      onChange={set('message')}
                    />
                    {fieldErrors.message && <span className="contact-field-error">{fieldErrors.message}</span>}
                  </div>

                  {error && (
                    <p style={{ fontFamily: 'DM Sans, sans-serif', color: '#e05555', fontSize: '0.9rem', margin: 0 }}>
                      {error}
                    </p>
                  )}

                  <button type="submit" className="contact-submit" disabled={loading}>
                    {loading ? 'Sending…' : 'Send message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </div>
      {/* <BrandsSection brands={brands} isLoading={brandsLoading} /> */}
    </Layout>
  );
}
